import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import axiosInstance from '../../AxiosInstance';

const EditImageDialog = ({ open, onClose, onSubmitSuccess, fileToEdit }) => {
  const { register, handleSubmit, setValue, formState: { errors }, reset } = useForm();
  const [selectedImage, setSelectedImage] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');

  // Set form values when fileToEdit changes
  useEffect(() => {
    if (fileToEdit) {
      setValue('name', fileToEdit.name);
      setValue('title', fileToEdit.title);
      setValue('description', fileToEdit.description || '');
      setValue('page', fileToEdit.page);
      setPreviewUrl(fileToEdit.photoUrl || '');
    }
  }, [fileToEdit, setValue]);

  const handleImageChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setSelectedImage(file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  const onSubmit = async (data) => {
    setIsSubmitting(true);

    try {
      const formData = new FormData();
      formData.append('name', data.name);
      formData.append('title', data.title);
      formData.append('description', data.description || '');
      formData.append('page', data.page);

      if (selectedImage) {
        formData.append('file', selectedImage);
      }

      const response = await axiosInstance.put(
        `/files/edit/${fileToEdit.id}`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' }
        }
      );

      onSubmitSuccess(response.data);
      reset();
      setSelectedImage(null);
      setPreviewUrl('');
      onClose();
    } catch (error) {
      console.error('Error submitting form:', error);
      alert(`Submission failed: ${error.response?.data?.message || error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <h2 className="text-xl font-bold mb-6 text-gray-900 dark:text-white">
            {fileToEdit ? 'Edit Information' : 'Add New Information'}
          </h2>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            {/* ... [rest of your form fields remain the same] ... */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-gray-700 dark:text-gray-300 text-sm font-medium mb-1" htmlFor="name">
                  Name*
                </label>
                <input
                  id="name"
                  type="text"
                  {...register('name', { required: 'Name is required' })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                />
                {errors.name && <p className="mt-1 text-red-500 text-xs">{errors.name.message}</p>}
              </div>

              <div>
                <label className="block text-gray-700 dark:text-gray-300 text-sm font-medium mb-1" htmlFor="title">
                  Title*
                </label>
                <input
                  id="title"
                  type="text"
                  {...register('title', { required: 'Title is required' })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                />
                {errors.title && <p className="mt-1 text-red-500 text-xs">{errors.title.message}</p>}
              </div>

              <div className="md:col-span-2">
                <label className="block text-gray-700 dark:text-gray-300 text-sm font-medium mb-1" htmlFor="description">
                  Description
                </label>
                <textarea
                  id="description"
                  {...register('description')}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                  rows={3}
                />
              </div>


              <div>
                <label className="block text-gray-700 dark:text-gray-300 text-sm font-medium mb-1" htmlFor="page">
                  Page*
                </label>
                <select
                  id="page"
                  {...register('page', { required: 'Page is required' })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                >
                  <option value="">Select a page</option>
                  <option value="HOME">HOME</option>
                  <option value="ABOUT">ABOUT</option>
                  <option value="NEWS">NEWS</option>
                  <option value="ALUMNI">ALUMNI</option>
                  <option value="GALLERY">GALLERY</option>
                  <option value="ORGANIZATION">ORGANIZATION</option>
                  <option value="SEMINARIAN">SEMINARIAN</option>
                  <option value="WAWATA">WAWATA</option>
                </select>
                {errors.page && <p className="mt-1 text-red-500 text-xs">{errors.page.message}</p>}
              </div>

              <div>
                <label className="block text-gray-700 dark:text-gray-300 text-sm font-medium mb-1" htmlFor="image">
                  Image
                </label>
                <input
                  id="image"
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                />
              </div>
              
              {previewUrl && (
              <div className="mt-2">
                <p className="text-sm text-gray-600 dark:text-gray-300 mb-1">Image Preview:</p>
                <div className="flex justify-center">
                  <img 
                    src={previewUrl} 
                    alt="Preview" 
                    className="max-h-40 max-w-full object-contain border rounded-md"
                  />
                </div>
              </div>
            )}





              {/* Other form fields... */}
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <button
                type="button"
                onClick={() => {
                  reset();
                  setSelectedImage(null);
                  setPreviewUrl('');
                  onClose();
                }}
                className="px-4 py-2 text-gray-700 bg-gray-100 dark:bg-gray-700 dark:text-white rounded-md hover:bg-gray-200 transition-colors disabled:opacity-50"
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:bg-blue-400"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Saving...
                  </span>
                ) : 'Save Changes'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EditImageDialog;